import { useMsal } from '@azure/msal-react';
import type { LabelProps } from '@fluentui/react-components';
import { Button, Field, InfoLabel, Text } from '@fluentui/react-components';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useState } from 'react';
import { parseFromPacificTimezoneDate } from '../../common/date/parse-from-pacific-timezone-date';
import { useCommonStyles, useStores } from '../../common/hooks';
import { getTooltipContentByFieldName } from '../../common/tooltip/tooltip-get-content';
import { ProgramDate } from '../../models';
import { CustomMessageBar, PageTitle } from '../shared-components';
import { PopoverDateTimePicker } from '../shared-components/date-time-picker';
import { FlexBox } from '../shared-components/flex-box';
import Loading from '../shared-components/Loading';
import './ProgramDatesPage.scss';

type ProgramDatesPageLoadingType = 'load' | 'update';

const ProgramDatesPageImpl: React.FC = () => {
  const {
    instance
  } = useMsal();

  const {
    apiStore,
    awardStore,
    programDatesPageUiStore,
  } = useStores();

  const commonStyles = useCommonStyles();

  const [isShowSaveSuccessToast, setShowSaveSuccessToast] = useState(false);
  const [isShowSaveFailedToast, setShowSaveFailedToast] = useState(false);
  const [isLoading, setIsLoading] = useState<ProgramDatesPageLoadingType>();

  const renderProgramDate = (title: string, programDate: ProgramDate | undefined) => {
    if (!programDate) return (<></>);

    const validationMessage = programDatesPageUiStore.validationMessages.get(programDate.dateTypeCode);
    return (
      <FlexBox
        isFullWidth
        className='program-date-inputs'
        direction='column'
      >
        <Text
          as='h3'
          size={500}
          weight='semibold'
        >
          {title}
        </Text>
        <Field
          label={{
            htmlFor: `${programDate.dateTypeCode}-start-date`,
            children: (_: unknown, props: LabelProps) => (
              <InfoLabel
                {...props}
                label={{
                  className: commonStyles.inputFieldLabel,
                }}
                info={{
                  children: getTooltipContentByFieldName('program-dates-page', 'startDate'),
                  className: commonStyles.tooltip,
                }}
              >
                Start date
              </InfoLabel>
            ),
          }}
        >
          <PopoverDateTimePicker
            id={`${programDate.dateTypeCode}-start-date`}
            dateTime={parseFromPacificTimezoneDate(programDate.startDate)}
            onDateTimeChange={(date) => programDatesPageUiStore.updateProgramDate(
              programDate.dateTypeCode,
              'startDate',
              date,
            )}
          />
        </Field>
        <Field
          label={{
            htmlFor: `${programDate.dateTypeCode}-end-date`,
            children: 'End date',
            className: commonStyles.inputFieldLabel,
          }}
        >
          <PopoverDateTimePicker
            id={`${programDate.dateTypeCode}-end-date`}
            dateTime={parseFromPacificTimezoneDate(programDate.endDate)}
            onDateTimeChange={(date) => programDatesPageUiStore.updateProgramDate(
              programDate.dateTypeCode,
              'endDate',
              date,
            )}
          />
        </Field>
        <Field
          label={{
            htmlFor: `${programDate.dateTypeCode}-display-start-date`,
            children: 'Display Start date',
            className: commonStyles.inputFieldLabel,
          }}
        >
          <PopoverDateTimePicker
            id={`${programDate.dateTypeCode}-display-start-date`}
            dateTime={parseFromPacificTimezoneDate(programDate.displayStartDate)}
            onDateTimeChange={(date) => programDatesPageUiStore.updateProgramDate(
              programDate.dateTypeCode,
              'displayStartDate',
              date,
            )}
          />
        </Field>
        <Field
          label={{
            htmlFor: `${programDate.dateTypeCode}-display-end-date`,
            children: 'Display End date',
            className: commonStyles.inputFieldLabel,
          }}
        >
          <PopoverDateTimePicker
            id={`${programDate.dateTypeCode}-display-end-date`}
            dateTime={parseFromPacificTimezoneDate(programDate.displayEndDate)}
            onDateTimeChange={(date) => programDatesPageUiStore.updateProgramDate(
              programDate.dateTypeCode,
              'displayEndDate',
              date,
            )}
          />
        </Field>
        {
          !!validationMessage && (
            <Text className='text--red'>
              {validationMessage}
            </Text>
          )
        }
      </FlexBox>
    );
  }

  const handleSaveProgramDate = async (): Promise<void> => {
    programDatesPageUiStore.clearValidationMessages();
    const isValid = programDatesPageUiStore.validateAllProgramDates();

    if (isValid) {
      setIsLoading('update');

      const validProgramDates = programDatesPageUiStore.editingProgramDates.filter(
        programDate =>
          programDate.startDate &&
          programDate.endDate &&
          programDate.displayStartDate &&
          programDate.displayEndDate
      );

      try {
        await Promise.all(
          validProgramDates.map(async (programDate) => {
            const programDateRef = await apiStore.updateProgramDate(
              instance,
              awardStore.defaultProgramRef?.programId ?? 0,
              programDate
            );
            programDate.programDateId = programDateRef.programDateId;
            return programDateRef;
          })
        );

        await getProgramDatesForStaff();

        setShowSaveFailedToast(false);
        setShowSaveSuccessToast(true);
        setTimeout(() => {
          setShowSaveSuccessToast(false);
        }, 4000);
      } catch (error) {
        // TODO - handle error
        setShowSaveFailedToast(true);
        setShowSaveSuccessToast(false);
      } finally {
        setIsLoading(undefined);
      }
    } else {
      setShowSaveFailedToast(true);
    }
  }

  const getDefaultProgramRef = async () => {
    try {
      const programRef = await apiStore.getDefaultProgramRef(instance);
      awardStore.setDefaultProgramRef(programRef);
    } catch (error) {
      // TODO - handle error
    }
  }

  const getProgramDatesForStaff = async () => {
    if (!awardStore.defaultProgramRef) return;

    try {
      const programDates = await apiStore.getProgramDatesForStaff(instance, awardStore.defaultProgramRef.programId);
      programDatesPageUiStore.setProgramDates(awardStore.defaultProgramRef.programId, programDates);
    } catch (error) {
      // TODO - handle error
    }
  };

  const preloadData = async () => {
    setIsLoading('load');

    await getDefaultProgramRef();
    await getProgramDatesForStaff();

    setIsLoading(undefined);
  }

  useEffect(() => {
    preloadData();
    return () => {
      programDatesPageUiStore.resetStates();
    };
  }, []);

  return (
    <FlexBox
      isFullWidth
      direction='column'
      aligns='start'
      className='program-dates-page'
    >
      {
        isShowSaveSuccessToast &&
        <CustomMessageBar
          message={'Your date updates are saved!'}
          notificationTypeCode='success'
          showDismissButton
        />
      }
      {
        isShowSaveFailedToast &&
        <CustomMessageBar
          message={'Save failed! Please check validation message(s) on date fields.'}
          notificationTypeCode='error'
          showDismissButton
          onDismissClicked={() => setShowSaveFailedToast(false)}
        />
      }
      {
        isLoading && <Loading />
      }
      <PageTitle
        title='Programs'
        subtitle='Microsoft AI Cloud Partner Program'
      />
      <Button
        appearance='primary'
        onClick={handleSaveProgramDate}
      >
        Save
      </Button>
      <FlexBox
        wrap
        content='space-between'
        className='program-dates-page__program-dates'
      >
        {renderProgramDate('Nomination window', programDatesPageUiStore.getProgramDate('Nomination-Window'))}
        {renderProgramDate('Nomination Evaluation window', programDatesPageUiStore.getProgramDate('Scrubbing-Window'))}
        {renderProgramDate('Judging window', programDatesPageUiStore.getProgramDate('Judging-Window'))}
        {renderProgramDate('Review & designation window', programDatesPageUiStore.getProgramDate('Review-&-Designate-Window'))}
        {renderProgramDate('Compliance and revisions window', programDatesPageUiStore.getProgramDate('Compliance-&-Revisions-Window'))}
        {renderProgramDate('Winners window', programDatesPageUiStore.getProgramDate('Winners-Window'))}
      </FlexBox>
    </FlexBox>
  );
}

export const ProgramDatesPage = observer(ProgramDatesPageImpl);
